import { BulkLineError } from './bulkConvert'

import { escapeCSV } from '@/utils/csv'

const UUID_IN_LINE = /(?<![0-9a-f-])[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(?![0-9a-f-])/i

export interface BulkMoveLine {
  lineNumber: number
  costCenterId: string
}

const splitCsvLine = (line: string): string[] => {
  const cells: string[] = []
  let cell = ''
  let quoted = false
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i]
    if (quoted && ch === '"' && line[i + 1] === '"') {
      cell += '"'
      i += 1
    } else if (ch === '"') {
      quoted = !quoted
    } else if (ch === ',' && !quoted) {
      cells.push(cell)
      cell = ''
    } else {
      cell += ch
    }
  }
  cells.push(cell)
  return cells
}

/**
 * Reads the `cost_center_id` column when a CSV header names one, else (or when that cell has no id) the first UUID
 * on the line, so CSV rows paste as-is; blank and `#` lines are skipped.
 */
export const parseCostCenterLines = (text: string): { lines: BulkMoveLine[]; errors: BulkLineError[] } => {
  const lines: BulkMoveLine[] = []
  const errors: BulkLineError[] = []
  const seen = new Set<string>()
  let column: number | null = null
  text.split(/\r?\n/).forEach((raw, idx) => {
    const lineNumber = idx + 1
    const trimmed = raw.trim()
    if (trimmed === '' || trimmed.startsWith('#')) return
    if (column === null && !UUID_IN_LINE.test(trimmed)) {
      const header = splitCsvLine(trimmed)
        .map((cell) => cell.trim())
        .indexOf('cost_center_id')
      if (header !== -1) {
        column = header
        return
      }
    }
    const match =
      (column === null ? null : UUID_IN_LINE.exec(splitCsvLine(trimmed)[column] ?? '')) ?? UUID_IN_LINE.exec(trimmed)
    if (!match) {
      errors.push({ lineNumber, message: 'No cost center id on this line' })
      return
    }
    const costCenterId = match[0].toLowerCase()
    if (seen.has(costCenterId)) {
      errors.push({ lineNumber, message: 'Duplicate cost center id' })
      return
    }
    seen.add(costCenterId)
    lines.push({ lineNumber, costCenterId })
  })
  return { lines, errors }
}

export type BulkMoveStatus = 'pending' | 'previewed' | 'committed' | 'failed'

export interface BulkMoveRow extends BulkMoveLine {
  status: BulkMoveStatus
  summary: string | null
  irreversible: boolean
  error: string | null
  idempotencyRoot: string
}

export const moveRowsToCommit = (rows: BulkMoveRow[]): BulkMoveRow[] => rows.filter((row) => row.status === 'previewed')

export const countIrreversible = (rows: BulkMoveRow[]): number => rows.filter((row) => row.irreversible).length

export const moveRowsToCsv = (rows: BulkMoveRow[]): string => {
  const header = ['cost_center_id', 'status', 'error', 'summary']
  const body = rows.map((row) =>
    [row.costCenterId, row.status, row.error ?? '', row.summary ?? ''].map(escapeCSV).join(',')
  )
  return [header.join(','), ...body].join('\n')
}

export interface Pace {
  perSecond: number
  maxInFlight: number
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

/**
 * Starts at most `pace.perSecond` items per second with at most `pace.maxInFlight` running, and starts none once
 * `shouldStop` returns true; items already running finish. `work` records its own failures; a rejection is ignored.
 */
export const runPaced = async <T>(
  items: T[],
  pace: Pace,
  work: (item: T) => Promise<void>,
  shouldStop: () => boolean = () => false,
  now: () => number = Date.now
): Promise<void> => {
  const gapMs = 1000 / pace.perSecond
  const running = new Set<Promise<void>>()
  let nextStartAt = now()
  for (const item of items) {
    while (running.size >= pace.maxInFlight) await Promise.race(running)
    const wait = nextStartAt - now()
    if (wait > 0) await sleep(wait)
    if (shouldStop()) break
    nextStartAt = Math.max(nextStartAt, now()) + gapMs
    const task = work(item)
      .catch(() => {})
      .finally(() => running.delete(task))
    running.add(task)
  }
  await Promise.all(running)
}

/**
 * Unexpected failures (5xx incl. network, rate limits, lost auth) as opposed to an org the backend refused with a
 * reason. `null` is an error that isn't an `ApiError`.
 */
export const isUnexpectedFailure = (status: number | null): boolean =>
  status === null || status === 401 || status === 403 || status === 429 || status === 499 || status >= 500

/** Trips once `limit` of the last `window` outcomes were unexpected failures. */
export class FailureBreaker {
  private outcomes: boolean[] = []

  constructor(
    private readonly limit: number,
    private readonly window: number
  ) {}

  record(unexpected: boolean): void {
    this.outcomes.push(unexpected)
    if (this.outcomes.length > this.window) this.outcomes.shift()
  }

  get tripped(): boolean {
    return this.outcomes.filter(Boolean).length >= this.limit
  }
}

const STORAGE_KEY = 'billing-ops.bulk-move-to-new-billing.v1'

export interface SavedBulkMove {
  text: string
  rows: BulkMoveRow[]
}

/** Saves the run so a reload resumes it; drops summaries if the full list doesn't fit. False when nothing fit. */
export const saveBulkMove = (saved: SavedBulkMove, storage: Storage = window.localStorage): boolean => {
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(saved))
    return true
  } catch {
    try {
      storage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          ...saved,
          rows: saved.rows.map((r) => ({ ...r, summary: null })),
        })
      )
      return true
    } catch {
      storage.removeItem(STORAGE_KEY)
      return false
    }
  }
}

export const loadBulkMove = (storage?: Storage): SavedBulkMove | null => {
  try {
    const raw = (storage ?? window.localStorage).getItem(STORAGE_KEY)
    if (raw === null) return null
    const saved = JSON.parse(raw) as SavedBulkMove
    return Array.isArray(saved.rows) && typeof saved.text === 'string' ? saved : null
  } catch {
    return null
  }
}

export const clearBulkMove = (storage?: Storage): void => {
  try {
    ;(storage ?? window.localStorage).removeItem(STORAGE_KEY)
  } catch {
    // Storage is unavailable, so there is nothing saved to clear.
  }
}
